import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid2 from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApiKey } from '../../contexts/ApiKeyContext';
import useTenant from '../../hooks/useTenant';
import { Team, TStateEnum } from '../../models/Tournament';
import { enroll, EnrollmentData, EnrollmentDetails, fetchEnrollmentDetails } from '../../services/EnrollmentService';
import { isValidEmail } from '../../utils/Validation';
import { AccountCreationFields, MIN_PASS_LENGTH } from './AccountCreationFields';
import { ConsentSection } from './ConsentSection';
import { GuestContactFields } from './GuestContactFields';
import { LoginOrGuestStep } from './LoginOrGuestStep';
import { TeamSelection } from './TeamSelection';

export const Enroll = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { tId, tLegalName } = useTenant();
  const { apiKey, userProfile } = useApiKey();
  const navigate = useNavigate();

  const [enrollmentDetails, setEnrollmentDetails] = useState<EnrollmentDetails>();

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number>();
  const [teamName, setTeamName] = useState('');
  const [numberOfPlayers, setNumberOfPlayers] = useState<number>();
  const [regulationsAgreed, setRegulationsAgreed] = useState(false);
  const [gdprAgreed, setGdprAgreed] = useState(false);
  const [marketingAgreed, setMarketingAgreed] = useState(false);

  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const [emailError, setEmailError] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ status: 'success' | 'error'; message: string }>();
  const [proceedAsGuest, setProceedAsGuest] = useState<boolean>();

  useEffect(() => {
    if (tournamentId) {
      setLoading(true);
      fetchEnrollmentDetails(tournamentId)
        .then(setEnrollmentDetails)
        .catch((error) => {
          console.error('Failed to fetch enrollment details:', error);
          setResult({ status: 'error', message: 'Rejestracja nie jest w tej chwili aktywna. Spróbuj ponownie później.' });
        })
        .finally(() => setLoading(false));
    }
  }, [tournamentId, apiKey]);

  const validateEmail = (email: string) => {
    setEmailError(isValidEmail(email) ? '' : 'Nieprawidłowy adres email');
  };

  const isFormValid = useMemo(() => {
    const baseValid = userProfile || (!!name && !!surname && !!email && !emailError && !!phone);
    const accountValid = !createAccount || (password.length >= MIN_PASS_LENGTH && password === repeatPassword);

    if (!regulationsAgreed || !gdprAgreed) return false;

    if (!userProfile) {
      return baseValid && !!teamName && numberOfPlayers !== undefined && numberOfPlayers > 0 && accountValid;
    }

    const hasSelectedTeam = selectedTeamId !== undefined;
    const hasNewTeam = !!teamName && numberOfPlayers !== undefined && numberOfPlayers > 0;

    return baseValid && (hasSelectedTeam || hasNewTeam);
  }, [
    name,
    surname,
    email,
    emailError,
    phone,
    teamName,
    numberOfPlayers,
    regulationsAgreed,
    gdprAgreed,
    createAccount,
    password,
    repeatPassword,
    userProfile,
    selectedTeamId,
  ]);

  const handleSubmit = () => {
    if (!isFormValid) return;

    const enrollmentData: EnrollmentData = {
      tournamentId: Number(tournamentId),
      tenantId: tId,
      name,
      surname,
      email: userProfile?.login ?? email,
      phone,
      teamName,
      noOfPlayers: numberOfPlayers!,
      regulationsAgreed,
      gdprAgreed,
      marketingAgreed,
      password: createAccount ? password : undefined,
      selectedTeamId,
    };

    setLoading(true);

    enroll(enrollmentData)
      .then((response) => {
        setResult({
          status: 'success',
          message: 'Dziękujemy za wysłanie zgłoszenia. Sprawdź proszę swoją skrzynkę mailową w celu potwierdzenia rejestracji.',
        });
      })
      .catch((error) => {
        setResult({
          status: 'error',
          message: 'Wystąpił błąd. Odśwież stronę i upewnij się czy podane przez Ciebie dane są prawidłowe.',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTeamClick = (team: Team) => {
    if (selectedTeamId === team.id) {
      setSelectedTeamId(undefined);
    } else {
      setSelectedTeamId(team.id);
    }
  };

  return (
    <Grid2 container rowSpacing={1.5}>
      <Grid2 size={{ xs: 12 }}>
        <Typography variant="h4">{enrollmentDetails?.name}</Typography>
      </Grid2>

      {enrollmentDetails && (enrollmentDetails.offerUrl || enrollmentDetails.offerDetails) && (
        <Grid2 size={{ xs: 12 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Oferta</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid2 container rowSpacing={2} justifyContent={'center'}>
                {enrollmentDetails.offerUrl && (
                  <Grid2 size={{ xs: 12, md: 8, xl: 6 }}>
                    <img src={enrollmentDetails.offerUrl} alt="Oferta" width={'100%'} />
                  </Grid2>
                )}
                {enrollmentDetails.offerDetails && (
                  <Grid2 size={{ xs: 12 }}>
                    {enrollmentDetails.offerDetails.split('\n').map((line, index) => (
                      <Typography key={index}>{line}</Typography>
                    ))}
                  </Grid2>
                )}
              </Grid2>
            </AccordionDetails>
          </Accordion>
        </Grid2>
      )}

      {enrollmentDetails?.regulations && (
        <Grid2 size={{ xs: 12 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Regulamin</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography dangerouslySetInnerHTML={{ __html: enrollmentDetails.regulations }} />
            </AccordionDetails>
          </Accordion>
        </Grid2>
      )}

      {enrollmentDetails && enrollmentDetails.tournamentState === TStateEnum.RegistrationOpen && (
        <>
          <Grid2 size={{ xs: 12 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Formularz zgłoszeniowy</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid2 container spacing={2}>
                  {userProfile ? (
                    <>
                      <Grid2 size={{ xs: 12 }}>
                        <Divider>Wybierz istniejącą drużynę:</Divider>
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <TeamSelection
                          teams={userProfile.ownedSoccerTeams ?? []}
                          selectedTeamId={selectedTeamId}
                          teamName={teamName}
                          numberOfPlayers={numberOfPlayers}
                          handleTeamClick={handleTeamClick}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <Divider>lub dodaj nową:</Divider>
                      </Grid2>
                    </>
                  ) : (
                    <>
                      {proceedAsGuest === undefined ? (
                        <LoginOrGuestStep onProceed={() => setProceedAsGuest(true)} />
                      ) : (
                        <GuestContactFields
                          name={name}
                          setName={setName}
                          surname={surname}
                          setSurname={setSurname}
                          email={email}
                          setEmail={setEmail}
                          emailError={emailError}
                          validateEmail={validateEmail}
                          phone={phone}
                          setPhone={setPhone}
                        />
                      )}
                    </>
                  )}
                  {(userProfile !== undefined || proceedAsGuest) && (
                    <>
                      <Grid2 size={{ xs: 12, sm: 8 }}>
                        <TextField
                          size="small"
                          label="Nazwa drużyny"
                          fullWidth
                          value={teamName}
                          onChange={(e) => setTeamName(e.target.value)}
                          disabled={selectedTeamId !== undefined}
                          required
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                          size="small"
                          label="Liczba zawodników"
                          type="number"
                          fullWidth
                          value={numberOfPlayers === undefined ? '' : numberOfPlayers}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            setNumberOfPlayers(value >= 0 ? value : 0);
                          }}
                          error={numberOfPlayers !== undefined && numberOfPlayers <= 0}
                          helperText={
                            numberOfPlayers !== undefined && numberOfPlayers <= 0
                              ? 'Liczba zawodników musi być większa od zera'
                              : ''
                          }
                          disabled={selectedTeamId !== undefined}
                          required
                          onFocus={(e) => {
                            e.target.select();
                          }}
                        />
                      </Grid2>
                    </>
                  )}
                  {userProfile === undefined && proceedAsGuest && (
                    <Grid2 size={{ xs: 12 }}>
                      <FormControlLabel
                        control={
                          <Checkbox size="small" checked={createAccount} onChange={(e) => setCreateAccount(e.target.checked)} />
                        }
                        label="Chcę założyć konto na platformie Turnieje.org."
                      />
                      <Typography variant="body2" sx={{ paddingLeft: '28px', fontSize: '0.8rem', color: 'text.secondary' }}>
                        Utworzenie konta pozwoli Ci zarządzać drużynami i używać ich w innych turniejach i wydarzeniach.
                      </Typography>
                    </Grid2>
                  )}
                  {createAccount && (
                    <AccountCreationFields
                      password={password}
                      setPassword={setPassword}
                      repeatPassword={repeatPassword}
                      setRepeatPassword={setRepeatPassword}
                    />
                  )}
                </Grid2>
              </AccordionDetails>
            </Accordion>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Zgody</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ConsentSection
                  regulationsAgreed={regulationsAgreed}
                  setRegulationsAgreed={setRegulationsAgreed}
                  gdprAgreed={gdprAgreed}
                  setGdprAgreed={setGdprAgreed}
                  marketingAgreed={marketingAgreed}
                  setMarketingAgreed={setMarketingAgreed}
                  tLegalName={tLegalName}
                />
              </AccordionDetails>
            </Accordion>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!isFormValid || loading || enrollmentDetails?.tournamentState !== TStateEnum.RegistrationOpen}
            >
              {loading ? 'Ładowanie...' : 'Zapisuję się!'}
            </Button>
          </Grid2>
        </>
      )}

      <Dialog open={!!result}>
        <DialogTitle>{`Rejestracja do turnieju - ${result?.status === 'success' ? '✅ Sukces' : '❌ Błąd'}`}</DialogTitle>
        <DialogContent>
          <Typography>{result?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate('/')}>Powrót na stronę główną</Button>
          {result?.status === 'error' && <Button onClick={() => window.location.reload()}>Odśwież</Button>}
        </DialogActions>
      </Dialog>
    </Grid2>
  );
};

export default Enroll;
